import Logger from 'app/services/logger';
import PropTypes from 'prop-types';
import React, { ReactElement, useContext, FunctionComponent, ElementType } from 'react';
import {
  css as emotionCss,
  cache as emotionCache,
  flush as emotionFlush,
  injectGlobal as emotionInjectGlobal,
} from 'emotion';
import { ObjectInterpolation } from '@emotion/serialize';
import { forEachKeyDeep } from 'app/utils/object';
import { isServerRendering } from 'app/utils/env';

// Dirty hack to tell emotion not to use sheet.insertRule() to insert styles, because JSDOM does not
// seem to support it.
//
// This can be removed if we use ReactDOMServer for SSR instead of rendering with ReactDOM and
// converting the DOM to a string.
if (isServerRendering()) {
  // @ts-ignore
  emotionCache.sheet.speedy(false);
}

export function reset() {
  emotionFlush();
}

type StylesComponent<StyleDefinitions> =
  FunctionComponent<{ children: StylesRenderProp<StyleDefinitions>, attributes?: IAttributes }>;
type StylesRenderProp<StyleDefinitions> =
  (css: StylesClassGetter<StyleDefinitions>) => ReactElement<any> | null;
type StylesClassGetter<StyleDefinitions> =
  (name: Extract<keyof StyleDefinitions, string>, attributes?: IAttributes) => string;

// Only allow Emotion's object interpolation syntax.
export interface IStyles extends ObjectInterpolation<undefined> {}

type MaybeStyles = IStyles | null | undefined | false;
type DynamicStyleDefinition = (attributes: IAttributes) => MaybeStyles | MaybeStyles[];
export type IStyleDefinition = IStyles | DynamicStyleDefinition;

export interface IAttributes { [key: string]: any; }

/**
 * Useful type to infer type of the `css` function passed to `children`
 * based on the type of the component returned from `createStyles`.
 * Useful for prop types when passing the `css` function to another
 * component, example:
 *
 * ```
 * const Styles = createStyles({ asdf: {} });
 * class Another extends PureComponent<{ css: ExtractStylesClassGetter<Styles> }> {
 *   render() { return <div className={css('asdf')} /> }
 * }
 * const Injecter = () => <Styles>{css => <Another css={css} />}</Styles>;
 * ```
 */
export type ExtractStylesClassGetter<T> = T extends StylesComponent<infer S> ? StylesClassGetter<S> : never;

/**
 * createStyles() takes CSS styles defined as a JSON object with the same syntax as Emotion (and
 * same syntax as Aphrodite, which we used to use). Also adds support for defining dynamic CSS
 * properties/values based on props passed to the component returned by createStyles.
 *
 * The main goals of this abstraction are:
 *
 * - Write the styles outside of the component's `render` method so they are static and can be
 *   shared across instances of the same component
 * - Put the conditional logic right next to the styles that get applied under that condition, so
 *   you don't have to jump between different places when you want to figure out why a style is/is not
 *   getting applied
 * - Generate/combine classname strings for you, so the implementation details of how the classes
 *   are written can be changed
 * - Be very flexible so it can replace all previous attempts at making abstractions for writing
 *   conditional styles
 *
 * The syntax for dynamic CSS looks like:
 *
 * ```
 * const MyComponent = ({ isFirst, isLast }) => (
 *   <Styles attributes={{ isFirst, isLast }}>{css =>
 *     <div className={css('myElement')}>
 *       My Component
 *     </div>
 *   }</Styles>
 * );
 *
 * const Styles = createStyles({
 *   // May also return a single object instead of an array of objects.
 *   myElement: ({ isFirst, isLast }) => [
 *     {
 *       margin: '1em 0',
 *     },
 *
 *     // False-y values in the returned array will be ignored.
 *     isFirst && { marginTop: '0' },
 *     isLast && { marginBottom: '0' },
 *   ],
 * });
 * ```
 */
// TODO: Allow optionally specifying type of attributes object
export function createStyles<StyleDefinitions extends { [key: string]: IStyleDefinition }>(
  styleDefinitions: StyleDefinitions,
): StylesComponent<StyleDefinitions> {
  const makeCss = makeMakeCss(styleDefinitions);

  // Create <Styles> component
  const Styles: StylesComponent<StyleDefinitions> = ({
    children,
    attributes: /* as */ sharedAttributes,
  }) => {
    const css = makeCss(sharedAttributes);

    return children(css);
  };

  Styles.propTypes = {
    children: PropTypes.func.isRequired,
    attributes: PropTypes.object,
  };

  return Styles;
}

function makeMakeCss<StyleDefinitions extends { [key: string]: IStyleDefinition }>(
  styleDefinitions: StyleDefinitions,
): ((sharedAttributes?: IAttributes) => StylesClassGetter<StyleDefinitions>) {
  const mapAttributesToClassNameFor: { [key: string]: (attributes: IAttributes) => string } = {};

  for (const [styleName, styleDefinition] of Object.entries(styleDefinitions)) {
    try {
      if (typeof styleDefinition !== 'function') {
        styleWarnings(styleDefinition);

        mapAttributesToClassNameFor[styleName] = () =>
          // We currently reset the style cache between renders for server side rendering, so we
          // cannot pre-calculate the className at the time `createStyles` is called because the
          // styles may need to be reinserted
          injectCss(styleName, styleDefinition);
      } else {
        const mapAttributesToDynamicStyles: DynamicStyleDefinition = styleDefinition;

        mapAttributesToClassNameFor[styleName] = (attributes) => {
          try {
            const dynamicStyles = mapAttributesToDynamicStyles(attributes);

            if (dynamicStyles && typeof dynamicStyles !== 'object') {
              throw new Error(
                'Dynamic style definition should return an object or array of objects',
              );
            }

            const mergedStyles = mergeStyles(dynamicStyles);

            const className = injectCss(styleName, mergedStyles);

            return className;
          } catch (error) {
            Logger.error(`Error in style definition for '${styleName}':`, error);
            return '';
          }
        };
      }
    } catch (error) {
      Logger.error(`Error in style definition for '${styleName}':`, error);
    }
  }

  const makeCss = (sharedAttributes?: IAttributes) => {
    // Create css() function
    const wrappedCss: StylesClassGetter<StyleDefinitions> = (styleName, attributes = {}) => {
      const mapAttributesToClassName = mapAttributesToClassNameFor[styleName];

      if (!mapAttributesToClassName) {
        return '';
      } else {
        const allAttributes = sharedAttributes ? { ...sharedAttributes, ...attributes } : attributes;
        return mapAttributesToClassName(allAttributes);
      }
    };

    return wrappedCss;
  };

  return makeCss;
}

// On dev, add label to the generated classname for debugging.
const addLabel = (
  styleName: string,
  styleDefinitionObject: IStyles,
): IStyles => (
  process.env.NODE_ENV !== 'production' ? { label: styleName, ...styleDefinitionObject } :
  /* else */ styleDefinitionObject
);

const injectCss = (
  styleName: string,
  styleDefinitionObject: IStyles,
): string => (
  emotionCss(addLabel(styleName, styleDefinitionObject))
);

const styleWarnings = (styles: IStyles | undefined | null | false) => {
  if (process.env.NODE_ENV !== 'production') {
    if (styles) {
      // On development, warn if you use ':hover' instead of useHoverState.
      forEachKeyDeep(
        styles,
        (key: string, value: any, parentKeys: string[]) => {
          if (key === ':hover') {
            Logger.warn(
              `:hover is deprecated, use useHoverState instead (in "${parentKeys.join(' > ')}")`,
            );
          }

          if (value === '') {
            Logger.warn(
              `Got empty string as CSS property value (in "${parentKeys.join(' > ')}")`,
            );
          }
        },
      );
    }
  }
};

const mergeStyles = (maybeStyles: MaybeStyles | MaybeStyles[]) => {
  const mergedStyles: IStyles = {};

  const mergeDynamicStyles = (styles: IStyles | undefined | null | false) => {
    styleWarnings(styles);

    Object.assign(mergedStyles, styles);
  };

  if (Array.isArray(maybeStyles)) {
    maybeStyles.forEach(mergeDynamicStyles);
  } else {
    mergeDynamicStyles(maybeStyles);
  }

  return mergedStyles;
};

export function makeStyledComponent<StyleDefinitions>(
  Styles: StylesComponent<StyleDefinitions>,
  Wrap: ElementType,
  style: Extract<keyof StyleDefinitions, string>,
  displayName: string,
) {
  const StyledComponent: FunctionComponent = ({ children }) => (
    <Styles>{css =>
      <Wrap className={css(style)}>{children}</Wrap>
    }</Styles>
  );
  StyledComponent.displayName = displayName;
  return StyledComponent;
}

/**
 * @deprecated Use `useCss` instead
 *
 * `useClasses()` takes CSS styles defined as a JSON object with the same syntax as Emotion (and
 * same syntax as Aphrodite, which we used to use).
 *
 * It is a hook, allowing you to define the styles dynamically based on props/state.
 *
 * The syntax for dynamic CSS looks like:
 *
 * ```
 * const MyComponent = ({ isFirst, isLast }) => {
 *   const classes = useClasses({
 *     // May also use a single object instead of an array of objects.
 *     //
 *     // Styles objects in the array will be merged together like `Object.assign()`.
 *     myElement: [
 *       {
 *         margin: '1em 0',
 *       },
 *
 *       // False-y values in the given array will be ignored.
 *       isFirst && { marginTop: '0' },
 *       isLast && { marginBottom: '0' },
 *     ],
 *   });
 *
 *   return (
 *     <div className={classes.myElement}>
 *       My Component
 *     </div>
 *   );
 * };
 *
 * ```
 */
export function useClasses<StyleDefinitions extends { [key: string]: IStyles | MaybeStyles[] }>(
  styleDefinitions: StyleDefinitions,
): { [K in keyof StyleDefinitions]: string } {
  const classNames: { [K in keyof StyleDefinitions]: string } = {} as { [K in keyof StyleDefinitions]: string };

  for (const styleName in styleDefinitions) {
    if (!styleDefinitions.hasOwnProperty(styleName)) { continue; }

    const styleDefinition = styleDefinitions[styleName];

    try {
      const mergedStyles = mergeStyles(styleDefinition);

      classNames[styleName] = injectCss(styleName, mergedStyles);
    } catch (error) {
      Logger.error(`Error in style definition for '${styleName}':`, error);
    }
  }

  return classNames;
}

const DummyContext = React.createContext(undefined);

export function useCss() {
  // We don't use the context yet, but we may in the future. This is just here
  // to make React trigger an error if someone tries to use useCss() outside of
  // a component.
  useContext(DummyContext);

  const css = (styleName: string, styleDefinition: IStyles | MaybeStyles[]): string => {
    const mergedStyles = mergeStyles(styleDefinition);

    return injectCss(styleName, mergedStyles);
  };

  return css;
}

export { keyframes } from 'emotion';
export { renderStylesToString } from 'emotion-server';

// Only allow raw strings, since the behavior is a little different from th
// main API (i.e. it does not generate unique class names for you), and because
// this is mainly intended for things like normalize.css, so it's much easier to
// just copy and paste the raw CSS.
export const injectGlobalCss = (rawGlobalCss: string) => {
  emotionInjectGlobal(rawGlobalCss);
};
